import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '@features/users/models/user.model';
import { BaseComponent } from '@shared-features/components/base-component/base.component';
import { FilterService } from '../../../filter/services/filter.service';
import { ConfigConstant } from '@shared/constants';
import { DateRangeType, FormAction, LookupType } from '@shared-features/enums/shared-enums.enum';
import { TabGroup } from '@shared-features/enums/tab.enum';
import { InvoiceStatus } from '@shared-features/enums/shipments.enum';
import { FunctionUtil } from '@shared/utils';
import { Lookup } from '@shared-features/models/Lookup.model';
import { UserApi } from '@shared-features/services';

@Component({
  selector: 'app-invoices-filter',
  templateUrl: './invoices-filter.component.html',
  styleUrls: ['./invoices-filter.component.scss'],
})
export class InvoicesFilterComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() action: Observable<FormAction>;

  form: FormGroup;
  filterActionSubscription: Subscription;

  params: any = null;
  creators: User[] = [];

  // enums
  InvoiceStatus = InvoiceStatus;
  SharedConstants = ConfigConstant;
  DateRangeType = DateRangeType;
  protected readonly LookupType = LookupType;

  InvoiceStatusOptions = Object.keys(InvoiceStatus).map((key) =>
    Object.assign(new Lookup(), {
      id: InvoiceStatus[key],
      value: this.translate('Enum.InvoiceStatus.' + InvoiceStatus[key]),
    })
  );

  constructor(
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private userAPI: UserApi
  ) {
    super();
  }

  ngOnInit(): void {
    this.getAllUsers();
    this.subscribeToQueryParams();
    this.subscribeToFilterAction();
    this.initForm();
  }

  getAllUsers() {
    this.load(this.userAPI.getAllUsers()).subscribe((creators) => {
      this.creators = creators;
      this.initForm();
    });
  }

  ngOnDestroy() {
    this.filterActionSubscription.unsubscribe();
  }

  subscribeToQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params && Object.keys(params).length !== 0) {
        this.params = params;
      }
    });
  }

  subscribeToFilterAction() {
    this.filterActionSubscription = this.action.subscribe((action: FormAction) => {
      switch (action) {
        case FormAction.Reset:
          this.resetForm();
          break;

        case FormAction.Cancel:
          this.cancelChanges();
          break;

        case FormAction.Submit:
          this.submitForm();
          break;
      }
    });
  }

  getFilterValuesBasedOnURLParams() {
    return {
      dateRange:
        this.params.dateRange === DateRangeType.ALL_TIME
          ? DateRangeType.ALL_TIME
          : String(this.params.dateRange),
      from: this.params.startFrom ? String(this.params.startFrom) : null,
      to: this.params.endAt ? String(this.params.endAt) : null,
      creatorId:
        this.params.creatorId === ConfigConstant.ALL
          ? ConfigConstant.ALL
          : Number(this.params.creatorId),
      status:
        this.params.status === ConfigConstant.ALL ? ConfigConstant.ALL : String(this.params.status),
    };
  }

  initForm() {
    if (this.params && Object.keys(this.params).length !== 0) {
      const filterValues = this.getFilterValuesBasedOnURLParams();
      this.form = new FormGroup({
        dateRange: new FormControl({
          range: filterValues.dateRange || DateRangeType.TODAY,
          startFrom: filterValues.from || '',
          endAt: filterValues.to || '',
        }),
        creatorId: new FormControl(
          this.creators.filter((creator) => filterValues.creatorId == creator.id)
        ),
        status: new FormControl(
          this.InvoiceStatusOptions.filter((status) => filterValues.status == status.id)[0]
        ),
      });
    } else {
      this.form = new FormGroup({
        dateRange: new FormControl({ range: DateRangeType.ALL_TIME, startFrom: '', endAt: '' }),
        creatorId: new FormControl(ConfigConstant.ALL),
        status: new FormControl(ConfigConstant.ALL),
      });
    }
  }

  cancelChanges() {
    if (this.params && Object.keys(this.params).length !== 0) {
      const filterValues = this.getFilterValuesBasedOnURLParams();
      this.form.setValue({
        dateRange: {
          range: filterValues.dateRange || DateRangeType.TODAY,
          startFrom: filterValues.from || '',
          endAt: filterValues.to || '',
        },
        creatorId: filterValues.creatorId,
        status: filterValues.status,
      });
    } else {
      this.resetForm();
    }
  }

  resetForm() {
    this.form.setValue({
      dateRange: { range: DateRangeType.ALL_TIME, startFrom: '', endAt: '' },
      creatorId: ConfigConstant.ALL,
      status: ConfigConstant.ALL,
    });

    this.submitForm();
  }

  submitForm() {
    const obj = this.form.value;
    if (this.form.controls.dateRange.value.startFrom && this.form.controls.dateRange.value.endAt) {
      obj['dateRange'].startFrom = FunctionUtil.convertDate(
        this.form.controls.dateRange.value.startFrom
      );
      obj['dateRange'].endAt = FunctionUtil.convertDate(this.form.controls.dateRange.value.endAt);
    }

    obj.from = obj.dateRange?.startFrom;
    obj.to = obj.dateRange?.endAt;
    obj.dateRange = obj.dateRange?.range;
    obj.creatorId = obj.creatorId.map((creator) => creator.id);
    obj.status = obj.status.id;
    obj.tabGroup = TabGroup.Invoices;

    this.filterService.navigateToTargetPageWithFilter(obj);
  }
}
